/**
 * Settings → Zmanim Settings → Special Zmanim
 * -------------------------------------------------------------------------
 * The organization-wide Hadlukas Haneiros (candle lighting) and Motzei
 * configuration. It is stored as `Zmanim_Display_Override_N` rows keyed by
 * `zmanimKey` ('shabbatHadlukasHaneiros' / 'shabbatMotzei'), carrying
 * candleOffsetMinutes / motzeiExitMode / motzeiExitMinutes / motzeiExitDegrees.
 *
 * When such a row is saved AND enabled, it overrides the built-in defaults for
 * any Minyanim-Roll minyan whose Timing Mode is "Dynamic Zman" and whose
 * selected Zman is one of those two keys — so the roll shows exactly the candle
 * / motzei time the organization configured in Settings.
 *
 * When no row is saved (or it is toggled off / deleted) `findSpecialZmanSettings`
 * returns null and callers keep their existing behaviour untouched.
 *
 * Shared by the live display (Display.vue → the roll-only zman resolvers, which
 * the Dashboard's embedded display reuses) and the roll editor's live preview so
 * both compute an identical time. The formulas mirror Special_Zmanim_Settings.vue.
 */

export const SPECIAL_ZMAN_CANDLE_KEY = 'shabbatHadlukasHaneiros';
export const SPECIAL_ZMAN_MOTZEI_KEY = 'shabbatMotzei';

/** Defaults mirror the Settings → Special Zmanim form's own fallbacks. */
export const DEFAULT_CANDLE_OFFSET_MINUTES = 18;
export const DEFAULT_MOTZEI_EXIT_MODE = 'fixedMinutes';
export const DEFAULT_MOTZEI_EXIT_MINUTES = 72;
export const DEFAULT_MOTZEI_EXIT_DEGREES = 8.5;

export interface SpecialZmanSettings {
  key: string;
  candleOffsetMinutes: number;
  motzeiExitMode: string;
  motzeiExitMinutes: number;
  motzeiExitDegrees: number;
}

export interface SpecialZmanResolveContext {
  /** Sunset (ISO) for the day being resolved. */
  sunsetIso?: string;
  /** kosher-zmanim ComplexZmanimCalendar already set to that day (degrees mode). */
  calendar?: any;
}

/** True for the two zmanim the Special Zmanim settings can drive. */
export function isSpecialZmanKey(key: unknown): boolean {
  return key === SPECIAL_ZMAN_CANDLE_KEY || key === SPECIAL_ZMAN_MOTZEI_KEY;
}

function positiveOr(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Normalize a kosher-zmanim getter result (Date | Luxon | Temporal) to ISO. */
export function specialZmanValueToIso(value: any): string {
  if (!value) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  if (typeof value.toISO === 'function' && value.isValid !== false) return value.toISO() || '';
  if (typeof value.toJSDate === 'function') {
    const d = value.toJSDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : '';
  }
  return '';
}

export function addSpecialZmanMinutesIso(iso: string, minutes: number): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const m = Number(minutes);
  return new Date(d.getTime() + (Number.isFinite(m) ? m : 0) * 60000).toISOString();
}

/**
 * The saved, enabled Special-Zmanim settings for `key`, or null when the
 * organization has not configured it (no row, deleted, or toggled off) — callers
 * then keep their own default behaviour.
 */
export function findSpecialZmanSettings(
  overrides: any,
  key: string,
): SpecialZmanSettings | null {
  if (!isSpecialZmanKey(key)) return null;
  const rows: any[] = Array.isArray(overrides) ? overrides : [];
  const row = rows.find(
    (o) => o && o.zmanimKey === key && o.isDeleted !== true && o.isActive !== false,
  );
  if (!row) return null;
  return {
    key,
    candleOffsetMinutes: positiveOr(row.candleOffsetMinutes, DEFAULT_CANDLE_OFFSET_MINUTES),
    motzeiExitMode: row.motzeiExitMode || DEFAULT_MOTZEI_EXIT_MODE,
    motzeiExitMinutes: positiveOr(row.motzeiExitMinutes, DEFAULT_MOTZEI_EXIT_MINUTES),
    motzeiExitDegrees: positiveOr(row.motzeiExitDegrees, DEFAULT_MOTZEI_EXIT_DEGREES),
  };
}

/**
 * The configured time for `key` as an ISO string:
 *   candle = sunset − candleOffsetMinutes
 *   motzei = sunset offset by (90 + motzeiExitDegrees)   ('degrees' mode)
 *          = sunset + motzeiExitMinutes                  ('fixedMinutes', default)
 * Returns '' when `settings` is null or it cannot be computed, so the caller
 * keeps its own fallback.
 */
export function resolveSpecialZmanIso(
  key: string,
  settings: SpecialZmanSettings | null,
  ctx: SpecialZmanResolveContext,
): string {
  if (!settings || !isSpecialZmanKey(key)) return '';
  const sunsetIso = ctx?.sunsetIso || '';

  if (key === SPECIAL_ZMAN_CANDLE_KEY) {
    return sunsetIso ? addSpecialZmanMinutesIso(sunsetIso, -settings.candleOffsetMinutes) : '';
  }

  // Motzei — degrees mode asks the calendar directly; anything else (and any
  // failure) falls back to the fixed minutes-after-sunset value.
  if (settings.motzeiExitMode === 'degrees' && typeof ctx?.calendar?.getSunsetOffsetByDegrees === 'function') {
    try {
      const iso = specialZmanValueToIso(
        ctx.calendar.getSunsetOffsetByDegrees(90 + settings.motzeiExitDegrees),
      );
      if (iso) return iso;
    } catch (e) {
      /* fall through to fixed minutes */
    }
  }
  return sunsetIso ? addSpecialZmanMinutesIso(sunsetIso, settings.motzeiExitMinutes) : '';
}

/**
 * One-shot helper: resolve `key` straight from the raw override rows, or '' when
 * the organization has not configured that special zman.
 */
export function resolveSpecialZmanIsoFromOverrides(
  overrides: any,
  key: string,
  ctx: SpecialZmanResolveContext,
): string {
  return resolveSpecialZmanIso(key, findSpecialZmanSettings(overrides, key), ctx);
}
